import { useEffect, useRef, useState } from 'react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
	type Instruction,
	extractInstruction,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/list-item';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { NAV4_PROJECT_MENU_ITEM_DRAG_AND_DROP } from './useProjectMenuItemDragAndDrop.tsx';

const useDropTargetForProjectsConditionally = () => {
	const ref = useRef<HTMLDivElement | null>(null);
	const [state, setState] = useState<'idle' | 'is-over'>();

	useEffect(() => {
		const element = ref.current;

		// might have no projects
		if (!element) {
			return;
		}

		return dropTargetForElements({
			element,
			canDrop: ({ source }) => {
				return source.data.type === NAV4_PROJECT_MENU_ITEM_DRAG_AND_DROP;
			},
			onDragStart() {
				setState('is-over');
			},
			onDragEnter() {
				setState('is-over');
			},
			onDragLeave() {
				setState('idle');
			},
			onDrop({ source, location }) {
				setState('idle');

				const dragging = source.data;
				const [innerMost] = location.current.dropTargets;

				if (!innerMost) {
					return;
				}
				const dropTargetData = innerMost.data;

				const instruction: Instruction | null = extractInstruction(dropTargetData);
				if (!instruction) {
					return;
				}

				// TODO: call mutation to reorder projects
				return { dragging, dropTargetData, instruction };
			},
		});
	});

	return { ref, state };
};

export const useDropTargetForProjects = functionWithCondition(
	() => fg('blu-6314-drag-and-drop-projects-sidebar-mouse'),
	useDropTargetForProjectsConditionally,
	() => ({ ref: { current: null }, state: undefined }),
);
